/**
 * The play-view session hook (task 20.3).
 *
 * Wires a {@link WsClient} to React state: it connects on mount, folds every
 * inbound push into the full journal (R7.1), tracks the transient "Rufus is
 * writing…" indicator (task 14.5 WRITING_INDICATOR_PUSH_TYPE) and the current
 * retry state (task 20.4 refines the waiting/retry UX — this provides the basic
 * hooks it builds on), and exposes the connection status. It disconnects on
 * unmount and reconnects automatically via the client's backoff.
 *
 * The WS client is injectable so tests drive it with a fake socket.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { WsClient, type ConnectionStatus, type WsClientOptions } from "./ws-client.js";
import {
  appendJournalEntry,
  hydrateJournal,
  pushToJournalEntry,
  type JournalEntry,
} from "./journal.js";
import type { RufusPush } from "./push-types.js";
import type { GetJournalResponse } from "@rufus/shared-types";

/** The transient retry state surfaced when a turn fails (R13.5). */
export interface RetryState {
  readonly turnId: string;
  readonly reason: string;
}

export interface CampaignSession {
  /** The full, unabridged journal (R7.1). */
  readonly journal: readonly JournalEntry[];
  /** True while a DM turn is generating ("Rufus is writing…"). */
  readonly writing: boolean;
  /**
   * The in-character cold-start waiting message ("Rufus consults the archives…"),
   * or null. Set while an Aurora cold start resumes (R15.2); cleared by the next
   * result/signal. Distinct from `writing` (a different, in-character cue).
   */
  readonly coldStartWaiting: string | null;
  /** The current retry state, or null. Cleared on the next successful signal. */
  readonly retry: RetryState | null;
  /**
   * The latest campaign seqToken observed from a committed result push, or
   * undefined until the first result arrives. The play view threads this into
   * {@link ApiClient.submitTurn} as the optimistic-concurrency token (R18.5).
   */
  readonly latestSeqToken: number | undefined;
  readonly status: ConnectionStatus;
  /** Manually clear the retry banner (e.g. after the player retries). */
  readonly clearRetry: () => void;
  /**
   * Seed the journal from a hydration read (Play-screen load AND on reconnect):
   * a "previously…" summary header plus the replayed conversation lines, merged
   * by id so a later live push for the same seq replaces in place (no
   * double-render). Also seeds `latestSeqToken` from the read's `nextSeq` so the
   * first turn carries a valid optimistic-concurrency token before any live
   * result arrives. Idempotent — re-hydrating with the same data is a no-op after
   * the merge.
   *
   * RECONNECT RECONCILIATION: this is also the catch-up path for a MISSED push.
   * A turn's result is delivered over the WebSocket, so if the socket was down
   * when the turn committed (a long turn, a navigated-away tab, a slept laptop)
   * the narration never arrived AND a stale "try again" retry banner was never
   * cleared. Re-hydrating on reconnect folds the committed narration in from the
   * persisted log; and because the read's `nextSeq` reflects committed progress,
   * a hydration that advances past the pending retry's turn CLEARS that stale
   * banner (the turn did resolve — the banner was a casualty of the dropped
   * socket, not a real failure).
   */
  readonly hydrate: (campaignId: string, history: GetJournalResponse) => void;
  /**
   * A monotonically-incrementing counter that bumps each time the socket
   * transitions INTO `open` AFTER the first connect — i.e. a RECONNECT. The play
   * view keys its journal catch-up read on this so a reconnect re-fetches the
   * log (recovering any result pushed while the socket was down) in addition to
   * the on-mount hydrate. The first open does not bump it (the mount hydrate
   * already covers the initial load).
   */
  readonly reconnectNonce: number;
  /**
   * A monotonically-incrementing counter the play view keys the MAP panel's
   * refetch on (map-staleness fix). The map is a SEPARATE GET /map, and keying
   * its refresh on `latestSeqToken` meant it only re-ran when a turn's save-
   * concurrency token advanced — so a mid-turn move, or a background ring-ahead
   * pre-author (which bumps no result token), left the browser map stale until
   * an unrelated event forced a refetch. This nonce bumps on EVERY `result` push
   * (even one with no committedSeqToken), on a `map_updated` push, and on a
   * hydrate (reconnect catch-up) — decoupling map refresh from the save token.
   */
  readonly mapRefreshNonce: number;
}

export type SessionClientOptions = Omit<WsClientOptions, "campaignId">;

/**
 * Build and manage a campaign session. `makeClient` is optional and injected in
 * tests; production passes the socket options and gets a real {@link WsClient}.
 */
export function useCampaignSession(
  campaignId: string,
  options: SessionClientOptions,
  makeClient?: (opts: WsClientOptions) => WsClient,
): CampaignSession {
  const [journal, setJournal] = useState<readonly JournalEntry[]>([]);
  const [writing, setWriting] = useState(false);
  const [coldStartWaiting, setColdStartWaiting] = useState<string | null>(null);
  const [retry, setRetry] = useState<RetryState | null>(null);
  const [latestSeqToken, setLatestSeqToken] = useState<number | undefined>(undefined);
  const [mapRefreshNonce, setMapRefreshNonce] = useState(0);
  const [status, setStatus] = useState<ConnectionStatus>("closed");
  const [reconnectNonce, setReconnectNonce] = useState(0);
  const clientRef = useRef<WsClient | null>(null);
  // A ref mirror of `latestSeqToken`, so the retry handler can read the current
  // committed seq WITHOUT a stale closure and WITHOUT an impure read inside a
  // setState updater (which StrictMode would double-invoke). Kept in sync on
  // every render — a render is the single source of truth for the latest value.
  const latestSeqTokenRef = useRef<number | undefined>(latestSeqToken);
  latestSeqTokenRef.current = latestSeqToken;
  // The journal `nextSeq` at the moment the current retry banner was raised, so a
  // later hydration that advances PAST it can tell the turn actually resolved and
  // clear the now-stale banner (reconnect reconciliation). Null while no banner.
  const retrySeqRef = useRef<number | null>(null);
  // Has the socket opened at least once? The FIRST open is the initial connect
  // (the mount hydrate covers it); every open AFTER that is a reconnect and bumps
  // `reconnectNonce` so the play view re-fetches the log to catch a missed push.
  const openedOnceRef = useRef(false);

  const hydrate = useCallback((cId: string, history: GetJournalResponse) => {
    setJournal((j) => hydrateJournal(j, cId, history.summary, history.entries));
    // Seed the concurrency token from the read's cursor, but never move it
    // BACKWARD past a token a live result already advanced (a result push could
    // land before/while the hydration read resolves). Max-merge.
    setLatestSeqToken((prev) =>
      prev === undefined ? history.nextSeq : Math.max(prev, history.nextSeq),
    );
    // A hydrate is the reconnect catch-up path — the map may have changed while
    // the socket was down (a missed map_updated / result). Bump the map-refresh
    // nonce so the MapPanel refetches GET /map after re-hydrating.
    setMapRefreshNonce((n) => n + 1);
    // RECONNECT RECONCILIATION: a stale retry banner from a turn whose RESULT
    // push we missed (socket was down at commit) must clear once we catch up. If
    // the hydrated log has advanced past the seq at which the banner was raised,
    // the turn committed after all — drop the banner. A banner raised by a GENUINE
    // failure sits at the current `nextSeq` (nothing committed after it), so this
    // only clears the stale case, never a real retry the player should still see.
    const raisedAt = retrySeqRef.current;
    if (raisedAt !== null && history.nextSeq > raisedAt) {
      retrySeqRef.current = null;
      setRetry(null);
    }
  }, []);

  // Stabilize the caller's live token provider behind a ref (design review
  // finding E): an inline `() => auth.idToken` changes identity every render, so
  // passing it straight into the effect would thrash the socket (reconnect on
  // every keystroke). The ref is updated each render; the WsClient is given a
  // STABLE wrapper that reads the latest provider, so a token REFRESH never
  // rebuilds the socket — only a campaign/url/auth-presence change does.
  const getTokenRef = useRef(options.getToken);
  getTokenRef.current = options.getToken;
  // Auth PRESENCE (not the token value): stable across a refresh (a new token is
  // still "present"), but flips on sign-out → the effect tears down the socket on
  // logout (finding F), without the per-refresh thrash a token-value dep caused.
  const hasToken = (options.getToken ? options.getToken() : options.token) != null;

  useEffect(() => {
    // SIGNED OUT → do NOT open a socket (fix 4): without a token the handshake is
    // rejected on arrival, so a tokenless connect would just spin "Reconnecting"
    // for a logged-out user. When `hasToken` later flips true (login) the effect
    // re-runs and builds the socket normally; when it flips false (logout) the
    // prior run's cleanup has already torn the socket down.
    if (!hasToken) {
      setStatus("closed");
      return;
    }
    const opts: WsClientOptions = {
      ...options,
      campaignId,
      // The stable live-token wrapper (reads the latest provider via the ref).
      getToken: () => getTokenRef.current?.() ?? null,
    };
    const client = makeClient ? makeClient(opts) : new WsClient(opts);
    clientRef.current = client;

    const offPush = client.onPush((push: RufusPush) => {
      // Writing indicator is transient UI state, not a journal line.
      if (push.type === "writing_indicator") {
        setWriting(push.writing);
        // Generation started → the cluster is awake; drop the cold-start cue.
        if (push.writing) setColdStartWaiting(null);
        return;
      }
      // The cold-start waiting cue ("Rufus consults the archives…") is transient
      // in-character UI state (R15.2), distinct from the writing indicator and
      // not a journal line. It clears on the next result/retry/writing signal.
      if (push.type === "cold_start_waiting") {
        setColdStartWaiting(push.message);
        return;
      }
      // A committed result clears any prior retry, the writing indicator, and
      // the cold-start waiting cue.
      if (push.type === "result") {
        retrySeqRef.current = null;
        setRetry(null);
        setWriting(false);
        setColdStartWaiting(null);
        // Track the campaign's committed seqToken so the next submitTurn carries
        // the current optimistic-concurrency token (R18.5).
        if (typeof push.committedSeqToken === "number") {
          setLatestSeqToken(push.committedSeqToken);
        }
        // A committed turn may have moved the party / changed geometry — refresh
        // the separate map fetch. Bump UNCONDITIONALLY (even with no committed
        // seqToken), since map refresh is decoupled from the save token. NOTE: do
        // NOT early-return here — a `result` push also folds into a narration
        // journal entry below (pushToJournalEntry), so the fall-through matters.
        setMapRefreshNonce((n) => n + 1);
      }
      // A dedicated map-change signal (a mid-turn move the result already covered
      // is harmless to double-refresh; the key case is the background ring-ahead
      // pre-author, which bumps no result token). Not a journal line — just a cue
      // to refetch GET /map.
      if (push.type === "map_updated") {
        setMapRefreshNonce((n) => n + 1);
        return;
      }
      if (push.type === "retry") {
        // Snapshot the seq at which the banner is raised (the latest committed
        // token we have seen, read from the ref mirror — no stale closure, no
        // impure updater). Reconnect reconciliation uses it: a later hydration
        // that advances PAST this clears the banner as stale (the turn committed
        // after all); a genuine failure leaves `nextSeq` at this value so the
        // banner correctly persists.
        retrySeqRef.current = latestSeqTokenRef.current ?? 0;
        setRetry({ turnId: push.turnId, reason: push.reason });
        setWriting(false);
        setColdStartWaiting(null);
      }
      const entry = pushToJournalEntry(push);
      if (entry) setJournal((j) => appendJournalEntry(j, entry));
    });
    const offStatus = client.onStatus((next) => {
      setStatus(next);
      // Detect a RECONNECT: the second and later transitions into `open`. The
      // first open is the initial connect (the mount hydrate covers it); a later
      // one means the socket dropped and came back, during which a result push
      // may have been missed — bump the nonce so the play view re-fetches the log.
      if (next === "open") {
        if (openedOnceRef.current) setReconnectNonce((n) => n + 1);
        else openedOnceRef.current = true;
      }
    });

    // A fresh socket lifecycle (campaign/token change): the next `open` is an
    // initial connect for THIS socket, not a reconnect, so reset the "opened
    // once" latch. (reconnectNonce itself is monotonic and never reset.)
    openedOnceRef.current = false;

    client.connect();
    return () => {
      offPush();
      offStatus();
      client.close();
      clientRef.current = null;
    };
    // Rebuild the socket on a campaign change, a URL change, or an auth-PRESENCE
    // change (sign-in ↔ sign-out) — NOT on every token refresh. The token is now
    // read LIVE via the stable `getToken` wrapper, so a refresh is picked up on
    // the next reconnect with no rebuild (avoiding the per-keystroke thrash a
    // token-VALUE dep caused). `hasToken` flips on logout so the socket tears down
    // for a signed-out user. The other option fields (timers/factory) are
    // test-only injections.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, options.url, hasToken]);

  return {
    journal,
    writing,
    coldStartWaiting,
    retry,
    latestSeqToken,
    status,
    clearRetry: () => {
      retrySeqRef.current = null;
      setRetry(null);
    },
    hydrate,
    reconnectNonce,
    mapRefreshNonce,
  };
}
